import { useState } from 'react';
import { line, Size, signatureEvidenceLine, signatureMethodLabel, t, type DocumentFileRow, type SignatureMethodValue, isEmailAddress } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { Button, DatePicker, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { post } from './api';
import { useDocumentToday } from '../../shell/ViewerZone';
import styles from './DocumentPage.module.css';
import { useDayText } from '../../shell/viewerDates';

export const DOCUMENT_SIGNATURES_PATH = '/document-signatures';
export const RECORD_SIGNED_COPY_PATH = '/document-signatures/manual';

/** One signature as the panel draws it; 🔨 the method is always shown, never a generic Signed badge. */
export interface SignatureRowView {
  id: string;
  documentRevision: number;
  method: SignatureMethodValue;
  methodLabel: string;
  evidenceLine: string;
  signedAt: string;
  signerName: string;
  signerEmail: string;
  recordedByName: string | null;
  vendorEnvelopeId: string | null;
}

/**
 * Record Signed Copy (piece 27, Customer signature; decisions 0009, 0519): the
 * customer returned the document signed, somebody uploaded it to this
 * document's files, and this names which file it is, who signed and when.
 *
 * 🔨 The modal never uploads. The returned file arrives through the document's
 * own Files panel, the same way every other file on the document does, and this
 * picks one of them — so there is one upload path and not a second.
 */
export function RecordSignedCopyModal({
  documentType,
  documentId,
  files,
  offersConvert = false,
  apiBaseUrl = '',
  onCancel,
  onDone,
}: {
  documentType: string;
  documentId: string;
  files: readonly DocumentFileRow[];
  offersConvert?: boolean;
  apiBaseUrl?: string;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const today = useDocumentToday();
  const [evidenceFileId, setEvidenceFileId] = useState(files[0]?.id ?? '');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signedAt, setSignedAt] = useState(today);
  const [convert, setConvert] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 🔒 A refused record reads inside the window, which stays open for the correction (decision 3063). */
  const [refusal, setRefusal] = useState<string | null>(null);

  const record = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post<{ salesOrderId: string | null }>(
      RECORD_SIGNED_COPY_PATH,
      { documentType, documentId, evidenceFileId, signerName: signerName.trim(), signerEmail: signerEmail.trim(), signedAt: `${signedAt}T00:00:00.000Z`, convert },
      apiBaseUrl,
    );
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onDone(answer.data.salesOrderId ? t('signature.recorded.converted') : t('signature.recorded'));
  };

  const ready = evidenceFileId !== '' && signerName.trim() !== '' && isEmailAddress(signerEmail);
  return (
    <Modal
      open
      title={t('signature.manual.title')}
      onCancel={onCancel}
      dataModal="recordSignedCopy"
      actions={[
        { kind: 'primary', label: t('signature.manual.act'), onAct: () => void record(), disabled: busy || !ready },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      {/* 🔨 No file on the document means there is nothing to name as the evidence, and the modal says so rather than offering an empty list. */}
      {files.length === 0 ? <p className={modalStyles.fieldLabel}>{t('signature.manual.noFiles')}</p> : (
        <Select size={Size.Medium}
          label={t('signature.manual.file')}
          value={evidenceFileId}
          onChange={setEvidenceFileId}
          options={files.map((file) => ({ value: file.id, label: line(file.filename, file.fileTypeName) }))}
          dataAttribute={{ 'data-signature-file': 'true' }}
        />
      )}
      <TextInput size={Size.Medium} label={t('signature.manual.signerName')} value={signerName} onChange={setSignerName} name="signatureSignerName" />
      <TextInput size={Size.Medium} label={t('signature.manual.signerEmail')} value={signerEmail} onChange={setSignerEmail} type="email" name="signatureSignerEmail" />
      <DatePicker size={Size.Small} label={t('signature.manual.signedAt')} value={signedAt} onChange={setSignedAt} mode="date" name="signatureSignedAt" />
      <p className={modalStyles.fieldLabel}>{t('signature.manual.hint')}</p>
      {offersConvert ? (
        <label className={modalStyles.fieldLabel}>
          <input type="checkbox" checked={convert} onChange={(event) => setConvert(event.target.checked)} data-signature-convert="true" />
          {t('signature.manual.convert')}
        </label>
      ) : null}
    </Modal>
  );
}

/**
 * The signatures on a document, drawn on its own page.
 *
 * 🔨 `method` is shown wherever a signature is shown, with the line saying what
 * the evidence behind it is (decision 0009). An electronic signature carries an
 * identity trail and a tamper-evident certificate; a manual one carries a
 * colleague's word, and presenting the two as equal evidence would be dishonest.
 */
export function SignaturesPanel({ rows, onRecord, mayRecord = true }: { rows: readonly SignatureRowView[]; onRecord?: () => void; mayRecord?: boolean }) {
  const dayText = useDayText();
  return (
    <div data-document-signatures={String(rows.length)}>
      {rows.length === 0 ? <p className={styles.muted}>{t('signature.none')}</p> : null}
      <DetailList look="separator">
        {rows.map((row) => (
          <DetailRow
            key={row.id}
            data-document-signature={row.id}
            data-signature-method={row.method}
            title={line(row.methodLabel, '·', row.signerName)}
            detail={[line(dayText(row.signedAt), t('signature.revision'), String(row.documentRevision), '·', row.recordedByName ?? row.vendorEnvelopeId ?? ''), row.evidenceLine]}
          />
        ))}
      </DetailList>
      {onRecord && mayRecord ? (
        <span data-signature-action="record">
          <Button size={Size.Small} text={[t('signature.manual.action')]} actions={[onRecord]} />
        </span>
      ) : null}
    </div>
  );
}

/** The two labels a screen may need without a row in hand, so nothing spells them itself. */
export const SIGNATURE_LABELS = {
  electronic: { label: signatureMethodLabel('Electronic'), evidence: signatureEvidenceLine('Electronic') },
  manual: { label: signatureMethodLabel('Manual'), evidence: signatureEvidenceLine('Manual') },
} as const;
